import { styled } from 'linaria/react';
import { css } from 'linaria';
import { Link } from 'react-router-dom';

import { FONTS, BREAKPOINTS } from '../../../styles/constants';
import { FONT_SIZE, LINE_HEIGHT, VISIBLE_LETTER_OPACITY } from './ThoughtAnimation/constants';

export const StyledActionHeightContainer = styled.div`
  display: flex;
  width: 100%;
  max-height: 206px;
  overflow: hidden;
  position: relative;
`;

export const StyledVirtualActionContainer = styled.div`
  overflow: hidden;
  pointer-events: none;
  visibility: hidden;
  width: 1px;

  > div {
    width: 396px;
  }
`;

export const StyledRealActionContainer = styled.div<{ enableConstantHeight?: boolean }>`
  left: 0;
  position: ${({ enableConstantHeight }) => (enableConstantHeight ? 'absolute' : 'static')};
  top: 0;
  width: 100%;
  padding: 0 0 12px 0;
`;

const getHorizontalPadding = ({
  isSpotlight,
  withoutPadding,
  isUserPicksImmersive,
  isSmall,
}: {
  isSpotlight?: boolean;
  withoutPadding?: boolean;
  isUserPicksImmersive?: boolean;
  isSmall?: boolean;
}) => {
  if (isSpotlight || withoutPadding || isUserPicksImmersive) {
    return 0;
  }

  if (isSmall) {
    return 12;
  }

  return 24;
};

export const StyledComment = styled.div<{
  isDetail?: boolean;
  fontColor: string;
  isSpotlight?: boolean;
  hasAnimation?: boolean;
  isQuickReactionOpen: boolean;
  withoutPadding?: boolean;
  isCardInFocus?: boolean;
  isReactionButtonMenuShown?: boolean;
  isReactionTokenModalOpen?: boolean;
  animationDuration?: string;
  isProfilePicks?: boolean;
  isUserPicksImmersive?: boolean;
  isSmall?: boolean;
}>`
  color: ${({ fontColor }) => fontColor};
  font-size: ${({ isSpotlight }) => `${isSpotlight ? 12 : 14}px`};
  height: auto;
  /* 165 px - is minimal height for avatar + reaction btn + reaction indicator */
  min-height: ${({ isDetail }) => (isDetail ? '165px' : 'auto')};
  padding: ${({ isDetail, isSpotlight, withoutPadding, isUserPicksImmersive, isSmall }) =>
    `${isDetail ? 20 : 0}px ${getHorizontalPadding({
      isSpotlight,
      withoutPadding,
      isUserPicksImmersive,
      isSmall,
    })}px 0`};
  user-select: none;
  width: 100%;

  & > div {
    align-items: flex-start;
    display: flex;
  }

  @media screen and (min-width: ${BREAKPOINTS.s}px) {
    font-size: 14px;
  }

  .circle .animate {
    animation-play-state: ${({
      isQuickReactionOpen,
      isReactionButtonMenuShown,
      isReactionTokenModalOpen,
      isCardInFocus,
    }) =>
      isQuickReactionOpen || isReactionButtonMenuShown || isReactionTokenModalOpen || !isCardInFocus
        ? 'paused'
        : 'running'};

    animation-duration: ${({ isDetail, animationDuration }) => (animationDuration || isDetail ? '24s' : '12s')};
  }
`;

export const StyledCommentInfo = styled.div<{ isSmall?: boolean }>`
  display: flex;
  flex: 1;
  font-family: ${FONTS.secondary};
  flex-direction: column;
  justify-content: center;
  position: relative;
  width: calc(80% - 30px);
  word-break: break-word;
  margin-right: 12px;

  & > div > span {
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
    -webkit-touch-callout: none;
  }

  a {
    color: inherit;
  }
`;

export const StyledToggle = styled.span`
  border-bottom: 1px solid grey;
  display: inline-block;
`;

export const StyledAvatarWithReactionContainer = styled.div<{
  isSmall?: boolean;
  isListCard?: boolean;
  isSpotlight?: boolean;
  isCarousel?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  margin-right: 8px;
  margin-top: ${({ isListCard, isSpotlight, isCarousel }) => (isListCard || isSpotlight || isCarousel ? 10 : 0)}px;
`;

export const APPEAR_ANIMATION = css`
  animation: 1s scaleIn ease-in-out forwards;
  animation-play-state: paused;
  visibility: hidden;
  pointer-events: none;
  scale: 0;
  opacity: 0;

  @keyframes scaleIn {
    0% {
      visibility: visible;
    }
    50% {
      opacity: 1;
    }
    100% {
      scale: 1;
      opacity: 1;
      visibility: visible;
      pointer-events: all;
      cursor: pointer;
    }
  }
`;

export const StyledReactionContainer = styled.div<{ hasLoadedReactions: boolean; isVirtualAction?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: column;
  min-height: ${({ isVirtualAction }) => (isVirtualAction ? '100px' : 'auto')};

  .${APPEAR_ANIMATION} {
    animation-play-state: ${({ hasLoadedReactions }) => (hasLoadedReactions ? 'running' : 'paused')};
  }
`;

export const StyledLink = styled(Link)<{
  size: number;
  disabled?: boolean;
  svglength?: number;
}>`
  align-items: center;
  align-self: flex-start;
  cursor: ${({ disabled }) => (disabled ? 'none' : 'pointer')};
  display: inline-flex;
  height: ${({ size }) => size}px;
  justify-content: center;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  text-decoration: none;
  position: relative;
  width: ${({ size }) => size}px;

  .circle {
    z-index: 20;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transform: rotate(-90deg);
    width: 100%;
  }

  circle {
    stroke-width: 4;
    fill-opacity: 0;
  }

  .animate {
    stroke-dasharray: ${({ svglength: circleLength }) => `${circleLength}`};
    stroke-dashoffset: ${({ svglength: circleLength }) => `${circleLength}`};
  }
`;

export const StrokeAnimation = css`
  animation-name: stroke;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;

  @keyframes stroke {
    to {
      stroke-dashoffset: 0;
    }
  }
`;

export const StyledAction = styled.span`
  cursor: pointer;
  color: var(--card-text-color, inherit);
  font-size: 12px;
  b {
    font-weight: 550;
  }
  line-height: 140%;
`;

export const StyledText = styled.p<{ $isUserPicksImmersive?: boolean; $isImmersive?: boolean }>`
  font-size: ${({ $isUserPicksImmersive, $isImmersive }) => ($isUserPicksImmersive || $isImmersive ? '20px' : '14px')};
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  margin: 0;
  white-space: pre-wrap;
  cursor: pointer;
  opacity: 0;
  animation: typewriter 0.02s steps(1) forwards;
  animation-delay: calc(var(--char-index) * 0.02s);
  font-family: ${FONTS.secondary};

  @keyframes typewriter {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledTextWrapper = styled.div`
  display: inline;
  position: relative;
`;

export const StyledTextChar = styled.span`
  opacity: 0;
  animation: typewriter 0.02s steps(1) forwards;
  animation-delay: calc(var(--char-index) * 0.02s);

  @keyframes typewriter {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledAnimationChar = styled.span<{ reserveSpaces?: boolean }>`
  font-size: ${FONT_SIZE}px;
  font-weight: 600;
  line-height: ${LINE_HEIGHT}px;
  opacity: 0;
  white-space: ${({ reserveSpaces }) => (reserveSpaces ? 'pre' : 'normal')};
  will-change: top, opacity;
  position: relative;
  top: 0;
`;

export const VISIBLE_LETTER = css`
  opacity: ${VISIBLE_LETTER_OPACITY};
`;

export const StyledAnimationWord = styled.span<{ isUnderlined?: boolean }>`
  position: relative;
  transform: translateY(2px);
  animation-timing-function: ease-in-out;
  animation-name: elevate;
  animation-fill-mode: forwards;
  animation-play-state: paused;
  text-decoration: ${({ isUnderlined }) => (isUnderlined ? 'underline' : 'none')};
  will-change: transform;
  max-width: fit-content;
`;

export const StyledFollowingStatus = styled.span`
  cursor: initial;
  flex-shrink: 0;
  font-weight: 400;
`;
export const StyledUserBio = styled.span<{ isSmall?: boolean }>`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ isSmall }) => (isSmall ? 1 : 2)};
  display: block;
  display: -webkit-box;
  font-weight: 400;
  cursor: initial;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${({ isSmall }) => (isSmall ? 'nowrap' : 'normal')};
`;

export const StyledActionContent = styled.div<{ isDetail?: boolean }>`
  margin: 0;
  font: ${({ isDetail }) => (isDetail ? `400 ${FONT_SIZE}px / ${LINE_HEIGHT}px ${FONTS.secondary}` : 'unset')};
`;

export const StyledHidePaddingTop = css`
  padding-top: 0;
`;

export const StyledTruncateWrapper = styled.div`
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  justify-content: space-between;
`;

export const StyledTruncateClickArea = styled.div`
  position: absolute;
  inset: 0;
  cursor: pointer;
  z-index: 10;
`;

export const StyledAlertWrap = styled.div`
  margin: 32px 0;
`;

export const StyledName = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledAvatarAnimationWrapper = styled.div<{ animate: boolean; isFeedItemRoute?: boolean }>`
  display: inline-block;
  opacity: ${(props) => {
    if (props.isFeedItemRoute) return 1;
    return props.animate ? 1 : 0;
  }};
  transform: ${(props) => {
    if (props.isFeedItemRoute) return 'scale(1)';
    return props.animate ? 'scale(1)' : 'scale(0)';
  }};
  transition: ${(props) =>
    props.isFeedItemRoute
      ? 'none'
      : 'opacity 0.4s cubic-bezier(0.6, 0.94, 0.58, 1.16), transform 0.4s cubic-bezier(0.6, 0.94, 0.58, 1.16)'};
`;
